import type {
  ArticulationProfile,
  Instrument,
  InstrumentControlSpec,
  InstrumentVariant,
  NoteRoleMetadata,
  RegionalTuningPreset,
  SampleLayerSpec,
  Tuning,
  TuningProfile
} from "./types";

export type {
  ArticulationProfile,
  CulturalAccuracyFlag,
  Instrument,
  InstrumentClassification,
  InstrumentControlSpec,
  InstrumentVariant,
  NoteRoleMetadata,
  RegionalTuningPreset,
  SampleLayerSpec,
  SoundDna,
  SoundFamily,
  Tuning,
  TuningProfile,
  UiModel,
  VariantToneProfile
} from "./types";

const pluckedBodyLayer: SampleLayerSpec = {
  id: "plucked-body-fallback",
  role: "body",
  fallbackOscillator: "triangle",
  pitchBehavior: "pitched",
  description: "Static fallback layer used until licensed samples are available."
};

const breathLayer: SampleLayerSpec = {
  id: "breath-tone-fallback",
  role: "body",
  fallbackOscillator: "sine",
  pitchBehavior: "pitched",
  description: "Breathy oscillator placeholder for flute-like instruments."
};

const drumLayer: SampleLayerSpec = {
  id: "drum-noise-fallback",
  role: "attack",
  fallbackOscillator: "noise",
  pitchBehavior: "unpitched",
  description: "Noise-envelope placeholder for membrane drum strokes."
};

const resonanceLayer: SampleLayerSpec = {
  id: "resonance-fallback",
  role: "resonance",
  fallbackOscillator: "sine",
  pitchBehavior: "pitched",
  description: "Synthetic resonance layer used by the first-phase audio scaffold."
};

const bronzeLayer: SampleLayerSpec = {
  id: "bronze-bar-fallback",
  role: "body",
  fallbackOscillator: "sine",
  pitchBehavior: "pitched",
  description: "Sine plus resonance placeholder for bronze key instruments."
};

const notePadControl: InstrumentControlSpec = {
  id: "note_pad",
  label: "Note pad",
  type: "keyboard",
  target: "pitch",
  public: true,
  description: "Keyboard and pointer trigger used by the current performance surface."
};

const approximationWarning =
  "This preset is an implementation scaffold. It must be reviewed against source material before it is presented as authoritative.";

const playableNoteLabelsByInstrument: Record<string, string[]> = {
  gayageum: ["C4", "D4", "E4", "G4", "A4", "C5"],
  erhu: ["D4", "E4", "G4", "A4", "B4", "D5"],
  oud: ["C4", "D4", "E4", "G4", "A4", "C5"],
  sitar: ["C4", "D4", "E4", "G4", "A4", "C5"],
  daegeum: ["C4", "D4", "E4", "G4", "A4", "C5"],
  ney: ["C4", "D4", "E4", "G4", "A4", "C5"],
  janggu: ["C4", "D4", "E4", "G4", "A4", "C5"],
  tabla: ["C4", "D4", "E4", "G4", "A4", "C5"],
  mbira: ["C4", "D4", "E4", "G4", "A4", "C5"],
  kora: ["C4", "D4", "E4", "G4", "A4", "C5"],
  yidaki_didgeridoo: ["C2", "D2", "E2", "G2", "A2", "C3"],
  gamelan_saron: ["C4", "D4", "E4", "G4", "A4", "C5"]
};

const tonicHzByInstrument: Record<string, number> = {
  erhu: 293.664768,
  yidaki_didgeridoo: 65.406391
};

const displayRolesByInstrument: Record<string, string[]> = {
  gayageum: ["string 1", "string 2", "string 3", "string 4", "string 5", "string 6"],
  erhu: ["open D role", "passing finger role", "upper color role", "open A role", "passing finger role", "upper D role"],
  oud: ["maqam tonic", "maqam color", "third degree", "dominant", "sixth degree", "octave"],
  sitar: ["Sa", "Re", "Ga", "Pa", "Dha", "Sa high"],
  daegeum: ["jigong 1", "jigong 2", "jigong 3", "jigong 4", "jigong 5", "jigong 6"],
  ney: ["maqam tonic", "maqam second", "maqam third", "dominant", "upper color", "octave"],
  janggu: ["gungpyeon low", "low head color", "low head color", "chaepyeon high", "high head color", "both-head cue"],
  tabla: ["bayan ge", "bayan ke", "dayan na", "dayan tin", "dayan ta", "dayan syahi"],
  mbira: ["left thumb lower", "left thumb upper", "right anchor", "right thumb response", "right extension", "upper response"],
  kora: ["left rank", "left rank", "right rank", "right rank", "thumb/index bridge", "upper rank"],
  yidaki_didgeridoo: ["drone fundamental", "low harmonic", "vocal color", "tongue pulse", "upper harmonic", "drone octave"],
  gamelan_saron: ["bar 1", "bar 2", "bar 3", "bar 4", "bar 5", "bar 6"]
};

function pitchFunctionForInstrument(instrumentId: string, index: number): NoteRoleMetadata["pitchFunction"] {
  if (instrumentId === "janggu" || instrumentId === "tabla") {
    return "stroke";
  }

  if (instrumentId === "kora") {
    return "rank";
  }

  if (instrumentId === "yidaki_didgeridoo") {
    return index === 0 ? "drone" : "passing";
  }

  if (index === 0) {
    return "tonic";
  }

  if (index === 3) {
    return "dominant";
  }

  return "passing";
}

function tuning(
  instrumentId: string,
  id: string,
  name: string,
  intervalsCents: number[],
  variantIds?: string[],
  description = "Relative pitch map for Timbroam data and UI integration tests."
): TuningProfile {
  const labels = playableNoteLabelsByInstrument[instrumentId] ?? playableNoteLabelsByInstrument.gayageum;
  const tonicHz = tonicHzByInstrument[instrumentId] ?? 261.625565;

  return {
    id,
    instrumentId,
    variantIds,
    name,
    rootNote: labels[0] ?? "C4",
    tonicHz,
    unit: "cents_from_tonic",
    notes: intervalsCents.map((cents, index) => ({
      label: labels[index] ?? `step_${index + 1}`,
      cents,
      role: index === 0 ? "tonic" : index === 3 ? "dominant" : "passing",
      displayRole: displayRolesByInstrument[instrumentId]?.[index],
      culturalRole: displayRolesByInstrument[instrumentId]?.[index],
      pitchFunction: pitchFunctionForInstrument(instrumentId, index),
      isApproximation: true,
      roleMetadata: {
        internalNote: labels[index] ?? `step_${index + 1}`,
        displayRole: displayRolesByInstrument[instrumentId]?.[index],
        culturalRole: displayRolesByInstrument[instrumentId]?.[index],
        pitchFunction: pitchFunctionForInstrument(instrumentId, index),
        isApproximation: true
      }
    })),
    detuneCents: {
      [labels[1] ?? "D4"]: intervalsCents[1] - 200,
      [labels[2] ?? "E4"]: intervalsCents[2] - 400,
      [labels[3] ?? "G4"]: intervalsCents[3] - 700,
      [labels[4] ?? "A4"]: intervalsCents[4] - 900,
      [labels[5] ?? "C5"]: intervalsCents[5] - 1200
    },
    intervalsCents,
    isApproximation: true,
    warning: approximationWarning,
    description
  };
}

function preset(
  instrumentId: string,
  id: string,
  name: string,
  region: string,
  intervalsCents: number[],
  description: string,
  variantIds?: string[]
): RegionalTuningPreset {
  const tonicHz = tonicHzByInstrument[instrumentId] ?? 261.625565;

  return {
    id,
    instrumentId,
    variantIds,
    name,
    culture: region,
    region,
    sourceType: "approximation",
    tonicHz,
    cents: intervalsCents,
    intervalsCents,
    isApproximation: true,
    warning: approximationWarning,
    description
  };
}

function articulation(
  instrumentId: string,
  id: string,
  name: string,
  techniques: ArticulationProfile["techniques"],
  variantIds?: string[]
): ArticulationProfile {
  return { id, instrumentId, variantIds, name, techniques };
}

const gayageumControls: InstrumentControlSpec[] = [
  notePadControl,
  {
    id: "left_hand_bend_lane",
    label: "Left-hand bend",
    type: "gesture",
    target: "pitch",
    gesture: "drag after pluck",
    public: true,
    description: "Models bridge-side left-hand pressure after the pluck."
  }
];

const daegeumJigongControls: InstrumentControlSpec[] = Array.from({ length: 6 }, (_, index) => ({
  id: `jigong_${index + 1}`,
  label: `Jigong ${index + 1}`,
  type: "button" as const,
  target: "pitch" as const,
  public: true,
  description: "Primary play button for one of the six finger holes."
}));

const instrumentsData: Instrument[] = [
  {
    id: "gayageum",
    name: "Gayageum",
    aliases: ["Korean zither"],
    family: "Plucked zither",
    classification: {
      family: "plucked_string",
      familyLabel: "Plucked string zither",
      soundProduction: "Plucked strings on movable bridges.",
      regionTags: ["Korea"],
      primaryMaterials: ["wood", "silk", "nylon", "wrapped modern strings"],
      hornbostelSachsHint: "312.22"
    },
    origin: "Korea",
    shortDescription:
      "A Korean plucked zither whose variant, string material, bridge layout, and left-hand technique strongly shape its sound.",
    culturalNote:
      "Sanjo, pungnyu/beomgeum, and modern expanded instruments should be handled as distinct practice contexts.",
    culturalFlags: [
      {
        id: "gayageum-terminology",
        type: "terminology",
        severity: "info",
        appliesTo: "variant",
        requiresReview: true,
        message:
          "Use Pungnyu gayageum or Beomgeum as the standard DB naming. Jeongak gayageum is kept only as a descriptive alias."
      }
    ],
    playableNotes: ["C4", "D4", "E4", "G4", "A4", "C5"],
    variants: [
      {
        id: "sanjo12",
        instrumentId: "gayageum",
        name: "Sanjo 12-string Gayageum",
        aliases: ["sanjo gayageum"],
        priority: "primary",
        region: "Korea",
        construction: "12 silk strings on movable bridges, narrower body, quick response.",
        performanceContext: "Sanjo, folk-derived solo repertoire, and contemporary chamber settings.",
        distinguishingFeatures: ["12 strings", "silk", "fast ornament response"],
        stringProfile: { stringCount: 12, material: "silk" },
        toneProfile: {
          attack: "quick tactile pluck",
          sustain: "medium",
          resonance: "wood body with close bridge response",
          brightness: "balanced",
          noise: "medium",
          description: "Focused attack and flexible pitch shading."
        },
        sampleLayers: [pluckedBodyLayer, resonanceLayer],
        controls: gayageumControls
      },
      {
        id: "pungryuBeomgeum12",
        instrumentId: "gayageum",
        name: "Pungnyu / Beomgeum 12-string Gayageum",
        aliases: ["beomgeum", "pungnyu gayageum", "jeongak gayageum descriptive alias only"],
        priority: "primary",
        region: "Korea",
        construction: "12 silk strings with a broader body and a slower resonance envelope.",
        performanceContext: "Pungnyu and court/literati repertoires with restrained articulation.",
        distinguishingFeatures: ["12 strings", "silk", "broader body", "restrained articulation"],
        stringProfile: { stringCount: 12, material: "silk" },
        toneProfile: {
          attack: "rounded pluck",
          sustain: "longer than sanjo model",
          resonance: "broad wooden body bloom",
          brightness: "dark",
          noise: "low",
          description: "Slower resonance and less aggressive ornament response."
        },
        sampleLayers: [pluckedBodyLayer, resonanceLayer],
        controls: gayageumControls
      },
      {
        id: "modern25",
        instrumentId: "gayageum",
        name: "Modern 25-string Gayageum",
        priority: "secondary",
        region: "Korea and contemporary stages",
        construction: "Expanded 25-string instrument with nylon or wrapped modern strings.",
        performanceContext: "New compositions, amplified ensembles, and education contexts.",
        distinguishingFeatures: ["25 strings", "modern string materials", "expanded range"],
        stringProfile: {
          stringCount: 25,
          material: "nylon/wrapped modern strings"
        },
        toneProfile: {
          attack: "clear and even",
          sustain: "medium-long",
          resonance: "clean modern projection",
          brightness: "bright",
          noise: "low",
          description: "Extended range with more uniform response."
        },
        sampleLayers: [pluckedBodyLayer, resonanceLayer],
        controls: gayageumControls
      },
      {
        id: "modern21",
        instrumentId: "gayageum",
        name: "Modern 21-string Gayageum",
        priority: "archiveOrRegional",
        region: "Korea and regional/archival modern practice",
        construction: "Expanded 21-string instrument, stored as lower-priority regional/archive data.",
        performanceContext: "Regional, educational, or transitional modern repertory contexts.",
        distinguishingFeatures: ["21 strings", "modern string materials", "lower catalog priority"],
        stringProfile: {
          stringCount: 21,
          material: "nylon/wrapped modern strings"
        },
        toneProfile: {
          attack: "clear",
          sustain: "medium",
          resonance: "modern zither body",
          brightness: "balanced",
          noise: "low",
          description: "Kept for compatibility with less common expanded layouts."
        },
        sampleLayers: [pluckedBodyLayer, resonanceLayer],
        controls: gayageumControls
      }
    ],
    tunings: [
      tuning(
        "gayageum",
        "gayageum_korean_pentatonic_approx",
        "Korean pentatonic approximation",
        [0, 204, 386, 702, 884, 1200],
        ["sanjo12", "pungryuBeomgeum12"],
        "Korean pentatonic study map. This is not a universal gugak tuning and must stay marked as an approximation."
      ),
      tuning(
        "gayageum",
        "gayageum_12yul_equalized_scaffold",
        "12-yul equalized scaffold",
        [0, 200, 400, 700, 900, 1200],
        ["sanjo12", "pungryuBeomgeum12", "modern25"],
        "MVP 100-cent yul scaffold only. Actual Korean tuning varies by piece, lineage, instrument, and performance context."
      ),
      tuning("gayageum", "gayageum_sanjo_study", "Sanjo study scaffold", [0, 204, 386, 702, 884, 1200], [
        "sanjo12"
      ]),
      tuning(
        "gayageum",
        "gayageum_pungnyu_study",
        "Pungnyu / Beomgeum study scaffold",
        [0, 190, 386, 690, 884, 1200],
        ["pungryuBeomgeum12"]
      )
    ],
    regionalTuningPresets: [
      preset(
        "gayageum",
        "gayageum_regional_sanjo_sketch",
        "Sanjo regional color sketch",
        "Korea",
        [0, 204, 386, 702, 884, 1200],
        "Placeholder for source-reviewed sanjo tuning families."
      )
    ],
    articulations: [
      articulation("gayageum", "gayageum_left_hand", "Gayageum left-hand techniques", [
        {
          id: "nonghyeon",
          label: "Nonghyeon",
          model: "upward_asymmetric_oscillation",
          gesture: "vertical or pressure wobble in the left-hand zone",
          description:
            "Nonghyeon bends upward asymmetrically; larger depth shortens decay and release in the fallback model."
        },
        {
          id: "jeonseong",
          label: "Jeonseong",
          model: "quick_left_hand_roll_pitch_turn",
          gesture: "short rolling pressure after pluck",
          description:
            "A quick left-hand roll and pitch turn, distinct from broad vibrato treatment."
        },
        {
          id: "toeseong",
          label: "Toeseong",
          model: "pitch_fall_release_rightward_motion",
          gesture: "release pressure with a rightward motion after the note",
          description:
            "Pitch falls as pressure releases; the UI gesture must include release/rightward motion, not only a downward pitch curve."
        }
      ])
    ],
    uiModel: {
      performanceSurface: "string_bridge",
      primaryPlayControlIds: ["note_pad", "left_hand_bend_lane"],
      warningPlacement: "right_panel",
      layoutNotes: "Bridge and left-hand pitch gestures should be variant-aware."
    },
    soundDna: {
      attack: "Plucked bridge-colored string onset",
      sustain: "Variant-dependent decay with hand-shaped pitch motion",
      resonance: "Wood body resonance and string coupling",
      ornamentation: "Jeonseong, toeseong, nonghyeon-family pitch shaping",
      identity: "Variant, repertoire, and lineage should remain visible in the interface"
    }
  },
  {
    id: "erhu",
    name: "Erhu",
    aliases: ["Chinese bowed spike fiddle"],
    family: "Bowed spike fiddle",
    classification: {
      family: "bowed_string",
      familyLabel: "Bowed string",
      soundProduction: "Bowed two-string spike fiddle with snakeskin membrane soundbox.",
      regionTags: ["China", "Chinese diaspora"],
      primaryMaterials: ["wood", "python skin", "silk", "steel"],
      isCulturallySensitive: true
    },
    origin: "China",
    shortDescription:
      "A bowed two-string instrument with no fingerboard, python skin membrane, and highly vocal vibrato control.",
    culturalNote:
      "Python skin can involve regulation, certification, and cross-border material restrictions. Timbroam should surface this as rights and documentation context.",
    culturalCaution:
      "Respect material sourcing and certification context when presenting erhu construction or sample metadata.",
    culturalFlags: [
      {
        id: "erhu-python-skin-certification",
        type: "rights",
        severity: "warning",
        appliesTo: "instrument",
        requiresReview: true,
        message:
          "Python skin membrane descriptions should include regulatory and certification considerations."
      }
    ],
    playableNotes: ["D4", "E4", "G4", "A4", "B4", "D5"],
    variants: [
      {
        id: "erhu_hexagonal",
        instrumentId: "erhu",
        name: "Hexagonal Erhu",
        priority: "primary",
        region: "China",
        construction: "Two strings, python skin membrane, hexagonal soundbox, no fingerboard.",
        performanceContext: "Solo, ensemble, opera, and contemporary concert contexts.",
        distinguishingFeatures: ["hexagonal soundbox", "python skin membrane", "hasFingerboard false"],
        stringProfile: {
          stringCount: 2,
          material: "steel, with silk/steel profile separation supported"
        },
        soundbox: { shape: "hexagonal", membrane: "python_skin", hasFingerboard: false },
        toneProfile: {
          attack: "bowed onset",
          sustain: "continuous",
          resonance: "membrane-filtered nasal body",
          brightness: "balanced",
          noise: "medium",
          description: "Centered erhu tone with expressive vibrato."
        },
        sampleLayers: [
          {
            ...resonanceLayer,
            id: "erhu-python-membrane-resonance",
            description: "Python skin membrane resonance placeholder."
          }
        ],
        controls: [notePadControl]
      },
      {
        id: "erhu_octagonal",
        instrumentId: "erhu",
        name: "Octagonal Erhu",
        priority: "secondary",
        region: "China",
        construction: "Two strings, python skin membrane, octagonal soundbox, no fingerboard.",
        performanceContext: "Regional and maker-specific erhu construction variants.",
        distinguishingFeatures: ["octagonal soundbox", "python skin membrane", "hasFingerboard false"],
        stringProfile: {
          stringCount: 2,
          material: "steel, with silk/steel profile separation supported"
        },
        soundbox: { shape: "octagonal", membrane: "python_skin", hasFingerboard: false },
        toneProfile: {
          attack: "bowed onset",
          sustain: "continuous",
          resonance: "slightly broader membrane body response",
          brightness: "balanced",
          noise: "medium",
          description: "Alternate soundbox shape preserved with neutral shape wording."
        },
        sampleLayers: [resonanceLayer],
        controls: [notePadControl]
      },
      {
        id: "erhu_rare_circular",
        instrumentId: "erhu",
        name: "Rare Circular Erhu",
        priority: "archiveOrRegional",
        region: "China",
        construction: "Two strings, python skin membrane, rare circular soundbox, no fingerboard.",
        performanceContext: "Archival or less-common construction reference.",
        distinguishingFeatures: ["rare circular soundbox", "python skin membrane", "hasFingerboard false"],
        stringProfile: {
          stringCount: 2,
          material: "steel, with silk/steel profile separation supported"
        },
        soundbox: { shape: "rare_circular", membrane: "python_skin", hasFingerboard: false },
        toneProfile: {
          attack: "bowed onset",
          sustain: "continuous",
          resonance: "maker-dependent membrane response",
          brightness: "dark",
          noise: "medium",
          description: "Lower-priority archival soundbox model."
        },
        sampleLayers: [resonanceLayer],
        controls: [notePadControl]
      }
    ],
    tunings: [
      tuning("erhu", "erhu_d_a_scaffold", "D-A fifth scaffold", [0, 200, 500, 700, 900, 1200])
    ],
    regionalTuningPresets: [
      preset("erhu", "erhu_concert_sketch", "Concert fifth sketch", "China", [0, 200, 500, 700, 900, 1200], "Approximate open-string reference.")
    ],
    articulations: [
      articulation("erhu", "erhu_bow_hand", "Erhu bow and left-hand techniques", [
        {
          id: "rou_xian",
          label: "Rou xian",
          model: "finger_rolling_vibrato",
          gesture: "continuous rolling left-hand motion",
          description: "Finger rolling vibrato model for erhu-style pitch and tone motion."
        }
      ])
    ],
    uiModel: {
      performanceSurface: "bowed_neck",
      primaryPlayControlIds: ["note_pad"],
      warningPlacement: "right_panel",
      layoutNotes: "No fingerboard should be shown; vibrato control belongs to the left-hand pitch lane."
    },
    soundDna: {
      attack: "Bowed attack through membrane-filtered body",
      sustain: "Continuous bow energy",
      resonance: "Python skin membrane and small wooden soundbox",
      ornamentation: "Rou xian finger rolling vibrato",
      identity: "Material sourcing, construction shape, and regional practice must remain explicit"
    }
  },
  {
    id: "oud",
    name: "Oud",
    aliases: ["Ud"],
    family: "Plucked fretless lute",
    classification: {
      family: "plucked_string",
      familyLabel: "Plucked string lute",
      soundProduction: "Plectrum-plucked courses on a fretless neck with pegbox.",
      regionTags: ["Middle East", "North Africa", "Turkey", "Iraq"],
      primaryMaterials: ["wood", "nylon", "gut", "wound strings"]
    },
    origin: "Middle East, North Africa, Turkey, and neighboring regions",
    shortDescription:
      "A fretless pear-shaped lute with regional variants, course-pair micro-detune behavior, and pegbox construction.",
    culturalNote:
      "Arabic, Turkish, and Iraqi oud traditions differ in tuning, build, repertoire, and technique. Iraqi oud must not be reduced to floating bridge alone.",
    culturalFlags: [
      {
        id: "oud-iraqi-not-floating-only",
        type: "terminology",
        severity: "warning",
        appliesTo: "variant",
        requiresReview: true,
        message: "Floating bridge is optional physical metadata, not the sole definition of Iraqi oud."
      }
    ],
    playableNotes: ["C4", "D4", "E4", "G4", "A4", "C5"],
    variants: [
      {
        id: "arabic_oud",
        instrumentId: "oud",
        name: "Arabic Oud",
        priority: "primary",
        region: "Arabic-speaking regions",
        construction: "Commonly described around 6 courses and 11 strings, fretless neck, bent-back pegbox.",
        performanceContext: "Maqam performance, taksim, ensemble, and song accompaniment.",
        distinguishingFeatures: ["6 courses", "11 strings", "pegbox", "deep rounded body"],
        stringProfile: {
          stringCount: 11,
          courseCount: 6,
          material: "nylon/gut/wound strings",
          microDetuneCents: [-3, 0, 2, -2, 1]
        },
        physicalMetadata: { pegbox: "bent-back pegbox" },
        toneProfile: {
          attack: "warm plectrum",
          sustain: "short-medium",
          resonance: "deep bowl body",
          brightness: "dark",
          noise: "medium",
          description: "Rounded body tone with audible course interaction."
        },
        sampleLayers: [pluckedBodyLayer, resonanceLayer],
        controls: [notePadControl]
      },
      {
        id: "turkish_oud",
        instrumentId: "oud",
        name: "Turkish Oud",
        priority: "primary",
        region: "Turkey",
        construction: "Often lighter and brighter than many Arabic oud builds, with fretless neck and pegbox.",
        performanceContext: "Makam performance, taksim, ensemble, and solo settings.",
        distinguishingFeatures: ["pegbox", "brighter response", "regional makam context"],
        stringProfile: {
          stringCount: 11,
          courseCount: 6,
          material: "nylon/gut/wound strings",
          microDetuneCents: [-2, 1, 3, -1, 1]
        },
        physicalMetadata: { pegbox: "bent-back pegbox" },
        toneProfile: {
          attack: "precise plectrum",
          sustain: "medium",
          resonance: "focused bowl body",
          brightness: "bright",
          noise: "medium",
          description: "Clearer upper register and agile plectrum response."
        },
        sampleLayers: [pluckedBodyLayer, resonanceLayer],
        controls: [notePadControl]
      },
      {
        id: "iraqi_oud",
        instrumentId: "oud",
        name: "Iraqi Oud",
        priority: "primary",
        region: "Iraq and Iraqi oud lineages",
        construction:
          "Fretless oud with pegbox; may use floating bridge, but the DB stores that only as optional physical metadata.",
        performanceContext: "Iraqi repertoire, solo performance lineages, and modern concert contexts.",
        distinguishingFeatures: [
          "pegbox",
          "Iraqi repertoire association",
          "maker and lineage-specific bowl response",
          "optional floating bridge metadata"
        ],
        stringProfile: {
          stringCount: 11,
          courseCount: 6,
          material: "nylon/gut/wound strings",
          microDetuneCents: [-4, 1, 2, -2, 2]
        },
        physicalMetadata: {
          pegbox: "bent-back pegbox",
          floatingBridge: true,
          bridgeNotes: "Floating bridge may appear in Iraqi oud builds, but it is not the sole variant definition."
        },
        toneProfile: {
          attack: "projecting plectrum",
          sustain: "medium",
          resonance: "variant-specific bowl and bridge response",
          brightness: "balanced",
          noise: "medium",
          description: "Lineage and repertoire data should drive the UI beyond bridge type."
        },
        sampleLayers: [pluckedBodyLayer, resonanceLayer],
        controls: [notePadControl]
      }
    ],
    tunings: [
      tuning("oud", "oud_course_microdetune", "Maqam course pair micro-detune scaffold", [0, 182, 386, 702, 884, 1200]),
      tuning(
        "oud",
        "maqam_rast_approx",
        "Maqam Rast approximation",
        [0, 204, 350, 498, 702, 884],
        ["arabic_oud", "iraqi_oud"],
        "Rast study map with neutral third/seventh values. Half-flat and neutral intervals vary by region, school, and melodic context."
      ),
      tuning(
        "oud",
        "maqam_hijaz_approx",
        "Maqam Hijaz approximation",
        [0, 112, 386, 498, 702, 814],
        ["arabic_oud", "turkish_oud", "iraqi_oud"],
        "Hijaz study map. This is not a fixed 24-TET prescription and must remain educational."
      ),
      tuning(
        "oud",
        "maqam_bayati_approx",
        "Maqam Bayati approximation",
        [0, 150, 316, 498, 702, 884],
        ["arabic_oud", "iraqi_oud"],
        "Bayati study map with variable second/third behavior by regional practice and phrase context."
      ),
      tuning(
        "oud",
        "maqam_nahawand_approx",
        "Maqam Nahawand approximation",
        [0, 200, 300, 500, 700, 800],
        ["arabic_oud", "turkish_oud", "iraqi_oud"],
        "Nahawand educational approximation for browser interaction, not a source-certified maqam tuning."
      ),
      tuning(
        "oud",
        "generic_24tet_educational",
        "Generic 24-TET educational grid",
        [0, 150, 350, 500, 700, 850],
        ["arabic_oud", "turkish_oud", "iraqi_oud"],
        "Educational 24-TET grid only. It must not be presented as actual maqam intonation."
      )
    ],
    regionalTuningPresets: [
      preset("oud", "oud_maqam_scaffold", "Maqam-aware placeholder", "Regional oud practice", [0, 182, 386, 702, 884, 1200], "Placeholder for future maqam-specific tuning logic.")
    ],
    articulations: [
      articulation("oud", "oud_plectrum", "Oud plectrum articulation", [
        {
          id: "course_pair_detune",
          label: "Course pair shimmer",
          model: "course_pair_micro_detune",
          gesture: "single pluck triggers paired strings",
          description: "Two strings in a course can be rendered with subtle micro-detune."
        }
      ])
    ],
    uiModel: {
      performanceSurface: "course_lute",
      primaryPlayControlIds: ["note_pad"],
      warningPlacement: "right_panel",
      layoutNotes: "Course pairs should be rendered as paired voices, not a single generic pluck."
    },
    soundDna: {
      attack: "Plectrum strike on doubled courses",
      sustain: "Short-medium lute decay",
      resonance: "Bowl body and course-pair shimmer",
      ornamentation: "Taksim phrasing, slides, and plectrum patterns",
      identity: "Regional naming, pegbox construction, course layout, and repertoire must be preserved"
    }
  },
  {
    id: "sitar",
    name: "Sitar",
    family: "Plucked fretted lute",
    classification: {
      family: "plucked_string",
      familyLabel: "Plucked string lute with sympathetic strings",
      soundProduction: "Mizraab-plucked strings with jawari buzz and sympathetic resonance.",
      regionTags: ["North India", "South Asia", "global concert practice"],
      primaryMaterials: ["wood", "gourd", "metal strings"]
    },
    origin: "North Indian classical and related practices",
    shortDescription:
      "A mizraab-played lute whose jawari buzz, chikari layout, meend range, and sympathetic resonance are core sound layers.",
    culturalNote:
      "Variant, gharana, repertoire, and pedagogy change the instrument role. Chikari and meend limits should be stored per variant.",
    culturalFlags: [
      {
        id: "sitar-mizraab-spelling",
        type: "terminology",
        severity: "info",
        appliesTo: "articulation",
        requiresReview: false,
        message: "Use mizraab consistently in UI and DB copy."
      }
    ],
    playableNotes: ["C4", "D4", "E4", "G4", "A4", "C5"],
    variants: [
      {
        id: "kharaj_pancham",
        instrumentId: "sitar",
        name: "Kharaj Pancham Sitar",
        priority: "primary",
        region: "North Indian classical practice",
        construction: "Bass kharaj configuration with sympathetic strings and jawari bridge.",
        performanceContext: "Raga performance with bass range and chikari punctuations.",
        distinguishingFeatures: ["kharaj bass string", "jawari buzz", "sympathetic resonance", "mizraab"],
        chikari: { strings: ["chikari_1", "chikari_2", "chikari_3"], tuning: ["Sa", "Sa", "Pa"] },
        meendLimits: { beginnerSoftLimitCents: 300, expertUpperLimitCents: 700 },
        toneProfile: {
          attack: "mizraab pluck",
          sustain: "long sympathetic shimmer",
          resonance: "jawari buzz plus taraf strings",
          brightness: "buzzing",
          noise: "medium",
          description: "Bass-capable sitar with rich sympathetic response."
        },
        sampleLayers: [
          pluckedBodyLayer,
          { id: "jawari_buzz", role: "buzz", fallbackOscillator: "sawtooth", pitchBehavior: "pitched", description: "Jawari buzz layer." },
          { id: "sympathetic_resonance", role: "sympathetic", fallbackOscillator: "sine", pitchBehavior: "pitched", description: "Taraf sympathetic resonance layer." }
        ],
        controls: [notePadControl]
      },
      {
        id: "gandhar_pancham",
        instrumentId: "sitar",
        name: "Gandhar Pancham Sitar",
        priority: "primary",
        region: "North Indian classical practice",
        construction: "Common concert configuration with chikari strings and sympathetic resonance.",
        performanceContext: "Raga performance, gat, jhala, and solo concert contexts.",
        distinguishingFeatures: ["gandhar pancham setup", "jawari buzz", "sympathetic resonance", "mizraab"],
        chikari: { strings: ["chikari_1", "chikari_2"], tuning: ["Sa", "Pa"] },
        meendLimits: { beginnerSoftLimitCents: 300, expertUpperLimitCents: 700 },
        toneProfile: {
          attack: "mizraab pluck",
          sustain: "long shimmer",
          resonance: "bright jawari and taraf response",
          brightness: "buzzing",
          noise: "medium",
          description: "Agile upper-register response with chikari punctuation."
        },
        sampleLayers: [
          pluckedBodyLayer,
          { id: "jawari_buzz", role: "buzz", fallbackOscillator: "sawtooth", pitchBehavior: "pitched", description: "Jawari buzz layer." },
          { id: "sympathetic_resonance", role: "sympathetic", fallbackOscillator: "sine", pitchBehavior: "pitched", description: "Taraf sympathetic resonance layer." }
        ],
        controls: [notePadControl]
      },
      {
        id: "surbahar",
        instrumentId: "sitar",
        name: "Surbahar",
        priority: "secondary",
        region: "North Indian classical practice",
        construction: "Larger bass lute related to sitar practice, with jawari and sympathetic resonance.",
        performanceContext: "Alap-focused and dhrupad-influenced instrumental contexts.",
        distinguishingFeatures: ["larger body", "lower range", "jawari buzz", "sympathetic resonance"],
        chikari: { strings: ["chikari_1", "chikari_2", "chikari_3"], tuning: ["Sa", "Sa", "Pa"] },
        meendLimits: { beginnerSoftLimitCents: 300, expertUpperLimitCents: 700 },
        toneProfile: {
          attack: "deep mizraab pluck",
          sustain: "long",
          resonance: "large body and sympathetic resonance",
          brightness: "buzzing",
          noise: "medium",
          description: "Lower and more expansive response than concert sitar."
        },
        sampleLayers: [
          pluckedBodyLayer,
          { id: "jawari_buzz", role: "buzz", fallbackOscillator: "sawtooth", pitchBehavior: "pitched", description: "Jawari buzz layer." },
          { id: "sympathetic_resonance", role: "sympathetic", fallbackOscillator: "sine", pitchBehavior: "pitched", description: "Taraf sympathetic resonance layer." }
        ],
        controls: [notePadControl]
      }
    ],
    tunings: [
      tuning("sitar", "sitar_sa_pa_scaffold", "Sa-Pa scaffold", [0, 204, 386, 702, 906, 1200]),
      {
        ...tuning(
          "sitar",
          "raga_yaman_approx",
          "Raga Yaman approximation",
          [0, 204, 408, 590, 702, 906],
          ["kharaj_pancham", "gandhar_pancham"],
          "Yaman browser approximation with tivra Ma context. Raga grammar cannot be reduced to a fixed scale."
        ),
        ragaContext: {
          arohana: ["Ni", "Re", "Ga", "Ma tivra", "Dha", "Ni", "Sa"],
          avarohana: ["Sa", "Ni", "Dha", "Pa", "Ma tivra", "Ga", "Re", "Sa"],
          drone: ["Sa", "Pa"],
          vadi: "Ga",
          samvadi: "Ni"
        }
      },
      {
        ...tuning(
          "sitar",
          "raga_bhairavi_approx",
          "Raga Bhairavi approximation",
          [0, 112, 316, 498, 702, 814],
          ["kharaj_pancham", "gandhar_pancham"],
          "Bhairavi browser approximation. Komal swara treatment and phrase grammar need source-aware review."
        ),
        ragaContext: {
          arohana: ["Sa", "re", "ga", "Ma", "Pa", "dha", "ni", "Sa"],
          avarohana: ["Sa", "ni", "dha", "Pa", "Ma", "ga", "re", "Sa"],
          drone: ["Sa", "Pa"],
          vadi: "Ma",
          samvadi: "Sa"
        }
      }
    ],
    regionalTuningPresets: [
      preset("sitar", "sitar_raga_context_placeholder", "Raga context placeholder", "North Indian classical practice", [0, 204, 386, 702, 906, 1200], "Future raga-aware pitch maps should replace this scaffold.")
    ],
    articulations: [
      articulation("sitar", "sitar_mizraab_meend", "Sitar mizraab and meend", [
        {
          id: "mizraab_pluck",
          label: "Mizraab pluck",
          model: "mizraab_attack_with_jawari_excitation",
          gesture: "pluck button or string gesture",
          description: "Mizraab spelling is normalized here."
        },
        {
          id: "meend",
          label: "Meend",
          model: "string_pull_pitch_curve",
          gesture: "horizontal drag after pluck",
          beginnerSoftLimitCents: 300,
          expertUpperLimitCents: 700,
          description: "Beginner UI soft limit is 300 cents; expert upper limit is 700 cents."
        }
      ])
    ],
    uiModel: {
      performanceSurface: "fretted_string",
      primaryPlayControlIds: ["note_pad"],
      warningPlacement: "right_panel",
      layoutNotes: "Jawari and sympathetic layers must remain visible in audio routing."
    },
    soundDna: {
      attack: "Mizraab pluck",
      sustain: "Long sympathetic resonance",
      resonance: "Jawari buzz and taraf strings",
      ornamentation: "Meend up to expert 700 cents with beginner 300-cent soft limit",
      identity: "Variant-specific chikari tuning and pedagogy shape the instrument model"
    }
  },
  {
    id: "daegeum",
    name: "Daegeum",
    family: "Breathy transverse bamboo flute",
    classification: {
      family: "breathy_transverse_flute",
      familyLabel: "Breathy transverse flute",
      soundProduction: "Side-blown bamboo flute with a reed membrane cheong.",
      regionTags: ["Korea"],
      primaryMaterials: ["bamboo", "reed membrane"]
    },
    origin: "Korea",
    shortDescription:
      "A Korean transverse bamboo flute with six primary finger holes and a cheong reed membrane.",
    culturalNote:
      "Sogeum is not a daegeum variant; it belongs to the broader samjuk family as an independent instrument.",
    culturalFlags: [
      {
        id: "daegeum-no-sogeum-variant",
        type: "terminology",
        severity: "warning",
        appliesTo: "variant",
        requiresReview: true,
        message: "Do not include sogeum as a daegeum variant."
      }
    ],
    playableNotes: ["C4", "D4", "E4", "G4", "A4", "C5"],
    variants: [
      {
        id: "jeongak_daegeum",
        instrumentId: "daegeum",
        name: "Jeongak Daegeum",
        priority: "primary",
        region: "Korea",
        construction: "Bamboo transverse flute: chwigu 1, jigong 6, cheongong 1, chilsunggong 2-5.",
        performanceContext: "Court and literati repertoire contexts.",
        distinguishingFeatures: ["six jigong", "cheong reed membrane", "larger jeongak context"],
        holes: { chwigu: 1, jigong: 6, cheongong: 1, chilsunggongRange: [2, 5] },
        physicalMetadata: {
          cheong: {
            membraneType: "reed_membrane",
            cheongTensionControlId: "cheong_tension",
            cheongExposureControlId: "cheong_exposure",
            breathPressureControlId: "breath_pressure"
          }
        },
        toneProfile: {
          attack: "breath onset",
          sustain: "continuous",
          resonance: "bamboo tube with cheong buzz",
          brightness: "breathy",
          noise: "high",
          description: "Broad breath tone and membrane color."
        },
        sampleLayers: [breathLayer, { id: "cheong_buzz", role: "buzz", fallbackOscillator: "sawtooth", pitchBehavior: "pitched", description: "Cheong reed membrane buzz." }],
        controls: [
          ...daegeumJigongControls,
          { id: "cheong_tension", label: "Cheong tension", type: "slider", target: "tone", public: true, min: 0, max: 1, description: "Controls reed membrane tension." },
          { id: "cheong_exposure", label: "Cheong exposure", type: "slider", target: "tone", public: true, min: 0, max: 1, description: "Controls how much membrane buzz enters the tone." },
          { id: "breath_pressure", label: "Breath pressure", type: "slider", target: "pressure", public: true, min: 0, max: 1, description: "Controls breath pressure separately from cheong behavior." }
        ]
      },
      {
        id: "sanjo_daegeum",
        instrumentId: "daegeum",
        name: "Sanjo Daegeum",
        priority: "primary",
        region: "Korea",
        construction: "Bamboo transverse flute: chwigu 1, jigong 6, cheongong 1, chilsunggong 2-5.",
        performanceContext: "Sanjo and folk-derived solo contexts.",
        distinguishingFeatures: ["six jigong", "cheong reed membrane", "sanjo response"],
        holes: { chwigu: 1, jigong: 6, cheongong: 1, chilsunggongRange: [2, 5] },
        physicalMetadata: {
          cheong: {
            membraneType: "reed_membrane",
            cheongTensionControlId: "cheong_tension",
            cheongExposureControlId: "cheong_exposure",
            breathPressureControlId: "breath_pressure"
          }
        },
        toneProfile: {
          attack: "direct breath onset",
          sustain: "continuous",
          resonance: "focused bamboo tube and cheong buzz",
          brightness: "breathy",
          noise: "high",
          description: "Flexible sanjo response with strong breath and membrane controls."
        },
        sampleLayers: [breathLayer, { id: "cheong_buzz", role: "buzz", fallbackOscillator: "sawtooth", pitchBehavior: "pitched", description: "Cheong reed membrane buzz." }],
        controls: [
          ...daegeumJigongControls,
          { id: "cheong_tension", label: "Cheong tension", type: "slider", target: "tone", public: true, min: 0, max: 1, description: "Controls reed membrane tension." },
          { id: "cheong_exposure", label: "Cheong exposure", type: "slider", target: "tone", public: true, min: 0, max: 1, description: "Controls how much membrane buzz enters the tone." },
          { id: "breath_pressure", label: "Breath pressure", type: "slider", target: "pressure", public: true, min: 0, max: 1, description: "Controls breath pressure separately from cheong behavior." }
        ]
      }
    ],
    tunings: [
      tuning("daegeum", "daegeum_jigong_scaffold", "Six jigong fingering scaffold", [0, 200, 386, 702, 884, 1200])
    ],
    regionalTuningPresets: [
      preset("daegeum", "daegeum_cheong_response_placeholder", "Cheong response placeholder", "Korea", [0, 200, 386, 702, 884, 1200], "Future model should separate fingering, breath pressure, and cheong response.")
    ],
    articulations: [
      articulation("daegeum", "daegeum_breath_cheong", "Daegeum breath and cheong", [
        {
          id: "cheong_color",
          label: "Cheong color",
          model: "reed_membrane_buzz_separate_from_breath_pressure",
          gesture: "cheong tension and exposure sliders",
          description: "Cheong tension, cheong exposure, and breath pressure remain separate controls."
        },
        {
          id: "yoseong",
          label: "Yoseong",
          model: "slow_breath_supported_pitch_oscillation",
          gesture: "held note plus slow pitch gesture",
          description: "Slow pitch motion over breath support, distinct from a generic fast vibrato."
        },
        {
          id: "toeseong",
          label: "Toeseong",
          model: "downward_pitch_release_curve",
          gesture: "release or downward drag after onset",
          description: "Downward pitch curve used as a daegeum articulation, separated from cheong buzz controls."
        }
      ])
    ],
    uiModel: {
      performanceSurface: "flute_holes",
      primaryPlayControlIds: daegeumJigongControls.map((control) => control.id),
      warningPlacement: "right_panel",
      layoutNotes: "Primary play buttons are the six jigong only."
    },
    soundDna: {
      attack: "Breath-led onset",
      sustain: "Continuous bamboo flute tone",
      resonance: "Bamboo tube plus cheong reed membrane",
      ornamentation: "Breath pressure, fingering, and membrane color",
      identity: "Samjuk taxonomy must not fold sogeum into daegeum variants"
    }
  },
  {
    id: "ney",
    name: "Ney",
    family: "Breathy end-blown flute",
    classification: {
      family: "breathy_end_blown_flute",
      familyLabel: "Breathy end-blown flute",
      soundProduction: "End-blown reed flute with embouchure-dependent harmonics.",
      regionTags: ["Arabic music", "Turkey", "Persian music"],
      primaryMaterials: ["reed"]
    },
    origin: "Middle Eastern, Turkish, and Persian musical practices",
    shortDescription:
      "A breathy end-blown reed flute whose regional variants differ in embouchure, tuning, and harmonic emphasis.",
    culturalNote:
      "Arabic, Turkish, and Persian ney practices should not be merged into a single generic flute model.",
    culturalFlags: [
      {
        id: "ney-regional-separation",
        type: "regional_variation",
        severity: "warning",
        appliesTo: "variant",
        requiresReview: true,
        message: "Arabic, Turkish, and Persian ney variants require separate embouchure and tuning data."
      }
    ],
    playableNotes: ["C4", "D4", "E4", "G4", "A4", "C5"],
    variants: [
      {
        id: "arabic_ney",
        instrumentId: "ney",
        name: "Arabic Ney",
        priority: "primary",
        region: "Arabic music contexts",
        construction: "End-blown reed flute with Arabic performance and tuning context.",
        performanceContext: "Taqsim, ensemble, and vocal accompaniment contexts.",
        distinguishingFeatures: ["end-blown reed", "breathy tone", "Arabic tuning context"],
        toneProfile: {
          attack: "airy edge",
          sustain: "continuous",
          resonance: "reed tube",
          brightness: "breathy",
          noise: "high",
          description: "Breath noise and flexible pitch are central."
        },
        sampleLayers: [breathLayer],
        controls: [notePadControl]
      },
      {
        id: "turkish_ney",
        instrumentId: "ney",
        name: "Turkish Ney",
        priority: "primary",
        region: "Turkey",
        construction: "End-blown reed flute with Turkish ney performance context.",
        performanceContext: "Makam, Sufi-associated, and concert contexts.",
        distinguishingFeatures: ["end-blown reed", "Turkish makam context", "breathy tone"],
        toneProfile: {
          attack: "soft airy edge",
          sustain: "continuous",
          resonance: "reed tube with focused upper partials",
          brightness: "breathy",
          noise: "high",
          description: "Makam context and breath control remain variant-specific."
        },
        sampleLayers: [breathLayer],
        controls: [notePadControl]
      },
      {
        id: "persian_ney",
        instrumentId: "ney",
        name: "Persian Ney",
        priority: "primary",
        region: "Persian classical practice",
        construction: "End-blown reed flute with interdental embouchure.",
        performanceContext: "Persian classical performance contexts.",
        distinguishingFeatures: ["interdental embouchure", "strong upper harmonics", "breathy tone"],
        physicalMetadata: {
          embouchure: "interdental",
          harmonicProfile: "strong_upper_harmonics"
        },
        toneProfile: {
          attack: "focused breath edge",
          sustain: "continuous",
          resonance: "reed tube with strong upper harmonics",
          brightness: "breathy",
          noise: "high",
          description: "Interdental embouchure and strong upper harmonics are stored separately."
        },
        sampleLayers: [breathLayer],
        controls: [notePadControl]
      }
    ],
    tunings: [
      tuning("ney", "ney_regional_scaffold", "Regional ney scaffold", [0, 182, 386, 702, 884, 1200]),
      tuning(
        "ney",
        "ney_maqam_rast_approx",
        "Shared Maqam Rast wind approximation",
        [0, 204, 350, 498, 702, 884],
        ["arabic_ney", "turkish_ney"],
        "Shared with oud maqam study data, with larger wind pitch instability in the audio engine."
      ),
      tuning(
        "ney",
        "ney_maqam_hijaz_approx",
        "Shared Maqam Hijaz wind approximation",
        [0, 112, 386, 498, 702, 814],
        ["arabic_ney", "turkish_ney", "persian_ney"],
        "Maqam/dastgah-adjacent browser study data only; ney embouchure makes pitch less stable than plucked strings."
      )
    ],
    regionalTuningPresets: [
      preset("ney", "ney_maqam_placeholder", "Maqam placeholder", "Arabic/Turkish/Persian regions", [0, 182, 386, 702, 884, 1200], "Future maqam or dastgah tuning logic should replace this scaffold.")
    ],
    articulations: [
      articulation("ney", "ney_breath", "Ney breath and embouchure", [
        {
          id: "breath_edge",
          label: "Breath edge",
          model: "edge_tone_breath_noise_mix",
          gesture: "breath pressure slider",
          description: "Breath noise and pitch edge are coupled but region-specific."
        }
      ])
    ],
    uiModel: {
      performanceSurface: "flute_holes",
      primaryPlayControlIds: ["note_pad"],
      warningPlacement: "right_panel",
      layoutNotes: "Persian interdental embouchure needs a distinct parameter set."
    },
    soundDna: {
      attack: "Breathy end-blown edge",
      sustain: "Continuous breath pressure",
      resonance: "Reed tube harmonics",
      ornamentation: "Embouchure, breath, and regional pitch inflection",
      identity: "Arabic, Turkish, and Persian contexts are separate data branches"
    }
  },
  {
    id: "janggu",
    name: "Janggu",
    family: "Hourglass membrane drum",
    classification: {
      family: "membrane_drum",
      familyLabel: "Membrane drum",
      soundProduction: "Two-headed hourglass drum with stick and hand strokes.",
      regionTags: ["Korea"],
      primaryMaterials: ["wood", "skin heads", "cord tension"]
    },
    origin: "Korea",
    shortDescription:
      "A Korean hourglass drum whose stroke IDs, oral labels, and head aliases must be stored separately.",
    culturalNote:
      "Jangdan presets can use different gu-eum labels across teachers and contexts; UI must not hard-code one universal syllable set.",
    culturalFlags: [
      {
        id: "janggu-jangdan-gueum-warning",
        type: "regional_variation",
        severity: "warning",
        appliesTo: "ui",
        requiresReview: true,
        message: "Jangdan preset gu-eum labels can vary by lineage, teacher, and repertoire."
      }
    ],
    playableNotes: ["C4", "D4", "E4", "G4", "A4", "C5"],
    variants: [
      {
        id: "pungmul_janggu",
        instrumentId: "janggu",
        name: "Pungmul Janggu",
        priority: "primary",
        region: "Korea",
        construction: "Hourglass body with two drumheads and adjustable tension cords.",
        performanceContext: "Pungmul, samulnori-related, and community performance contexts.",
        publicDescription: "Two-headed hourglass drum with contrasting head colors and stroke types.",
        distinguishingFeatures: ["strokeId separated from UI label", "bukpyeon/gungpyeon aliases", "conservative public material text"],
        drumMetadata: {
          headAliases: ["bukpyeon", "gungpyeon"],
          internalMaterials: ["dog_skin_traditional_possible", "cowhide_possible", "synthetic_possible"],
          casualMaterialDisclosure: "conservative",
          zones: ["gungpyeon", "chaepyeon", "rim", "center"]
        },
        toneProfile: {
          attack: "stick and hand stroke",
          sustain: "short",
          resonance: "hourglass body",
          brightness: "balanced",
          noise: "medium",
          description: "Contrasting drumhead roles with lineage-specific stroke labels."
        },
        sampleLayers: [drumLayer, resonanceLayer],
        controls: [
          { id: "gung_stroke", label: "Gung", type: "button", target: "articulation", public: true, description: "strokeId gung_stroke; UI label can vary by jangdan preset." },
          { id: "ddak_stroke", label: "Ddak", type: "button", target: "articulation", public: true, description: "strokeId ddak_stroke; UI label can vary by jangdan preset." }
        ]
      },
      {
        id: "court_janggu",
        instrumentId: "janggu",
        name: "Court / Jeongak Janggu",
        priority: "secondary",
        region: "Korea",
        construction: "Hourglass body adapted to court and jeongak contexts.",
        performanceContext: "Court, jeongak, and restrained ensemble settings.",
        publicDescription: "Two-headed hourglass drum with context-specific stroke vocabulary.",
        distinguishingFeatures: ["court context", "strokeId separated from UI label", "conservative public material text"],
        drumMetadata: {
          headAliases: ["bukpyeon", "gungpyeon"],
          internalMaterials: ["dog_skin_traditional_possible", "cowhide_possible", "synthetic_possible"],
          casualMaterialDisclosure: "conservative",
          zones: ["gungpyeon", "chaepyeon", "rim", "center"]
        },
        toneProfile: {
          attack: "controlled hand and stick articulation",
          sustain: "short",
          resonance: "hourglass body",
          brightness: "dark",
          noise: "low",
          description: "More restrained articulation profile for court contexts."
        },
        sampleLayers: [drumLayer, resonanceLayer],
        controls: [
          { id: "court_gung", label: "Gung", type: "button", target: "articulation", public: true, description: "strokeId court_gung; UI label can vary by preset." }
        ]
      }
    ],
    tunings: [tuning("janggu", "janggu_tension_scaffold", "Head tension scaffold", [0, 200, 400, 700, 900, 1200])],
    regionalTuningPresets: [
      preset("janggu", "janggu_jangdan_placeholder", "Jangdan label placeholder", "Korea", [0, 200, 400, 700, 900, 1200], "Stores warning that gu-eum labels can differ by preset and lineage.")
    ],
    articulations: [
      articulation("janggu", "janggu_strokes", "Janggu stroke IDs", [
        {
          id: "stroke_id_label_split",
          label: "Stroke ID / UI label split",
          model: "stable_stroke_id_variable_gueum_label",
          gesture: "button per stroke zone",
          description: "Internal strokeId remains stable while displayed gu-eum can vary by jangdan preset."
        }
      ])
    ],
    uiModel: {
      performanceSurface: "drum_heads",
      primaryPlayControlIds: ["gung_stroke", "ddak_stroke"],
      warningPlacement: "right_panel",
      layoutNotes: "Casual UI must use conservative public material wording."
    },
    soundDna: {
      attack: "Hand and stick membrane strikes",
      sustain: "Short tuned drum decay",
      resonance: "Hourglass body and cord-tensioned heads",
      ornamentation: "Jangdan-specific stroke patterns and gu-eum labels",
      identity: "Stroke IDs, head aliases, and public material wording are kept separate"
    }
  },
  {
    id: "tabla",
    name: "Tabla",
    family: "Paired membrane drums",
    classification: {
      family: "membrane_drum",
      familyLabel: "Membrane drum pair",
      soundProduction: "Dayan and bayan with syahi zones and pressure pitch modulation.",
      regionTags: ["North India", "Pakistan", "South Asian diaspora"],
      primaryMaterials: ["wood", "metal", "skin", "syahi"]
    },
    origin: "South Asia",
    shortDescription:
      "A paired drum system whose playing zones and bayan pressure behavior need explicit structured data.",
    culturalNote:
      "Tabla bols, gharana vocabulary, and drum construction vary by teacher, region, and performance context.",
    culturalFlags: [
      {
        id: "tabla-bol-context",
        type: "regional_variation",
        severity: "info",
        appliesTo: "articulation",
        requiresReview: true,
        message: "Bol labels and stroke pedagogy need source-aware mapping."
      }
    ],
    playableNotes: ["C4", "D4", "E4", "G4", "A4", "C5"],
    variants: [
      {
        id: "hindustani_tabla_pair",
        instrumentId: "tabla",
        name: "Hindustani Tabla Pair",
        priority: "primary",
        region: "North India, Pakistan, and diaspora practice",
        construction: "Dayan with center or near-center syahi; bayan with offset syahi.",
        performanceContext: "Hindustani classical, devotional, folk, and contemporary contexts.",
        distinguishingFeatures: ["dayan syahi center/near-center", "bayan syahi offset", "bayan pressure pitch rises"],
        drumMetadata: {
          zones: ["kinar", "chanti", "maidan", "syahi", "pressure_zone"],
          bayanPressure: {
            direction: "upward_on_press",
            pressEffect: "pitch_rises",
            releaseEffect: "falls_to_base"
          },
          bayanMaterials: ["copper", "brass", "steel", "wood_possible", "ceramic_or_earthen_possible_by_region"]
        },
        toneProfile: {
          attack: "finger and palm strokes",
          sustain: "short to resonant",
          resonance: "syahi-centered harmonic drum tone",
          brightness: "balanced",
          noise: "medium",
          description: "Zone-specific bols and bayan pressure pitch modulation."
        },
        sampleLayers: [drumLayer, resonanceLayer],
        controls: [
          { id: "dayan_zone", label: "Dayan zone", type: "zone", target: "articulation", zones: ["kinar", "chanti", "maidan", "syahi"], public: true, description: "Dayan zones include center/near-center syahi." },
          { id: "bayan_pressure", label: "Bayan pressure", type: "slider", target: "pressure", public: true, min: 0, max: 1, description: "Press raises pitch; release falls to base." }
        ]
      },
      {
        id: "concert_tabla_pair",
        instrumentId: "tabla",
        name: "Concert Tabla Pair",
        priority: "secondary",
        region: "Global concert practice",
        construction: "Tuned dayan and bayan pair prepared for stage performance.",
        performanceContext: "Amplified concert, studio, and cross-genre settings.",
        distinguishingFeatures: ["stage tuning", "same syahi placement logic", "bayan pressure pitch rises"],
        drumMetadata: {
          zones: ["kinar", "chanti", "maidan", "syahi", "pressure_zone"],
          bayanPressure: {
            direction: "upward_on_press",
            pressEffect: "pitch_rises",
            releaseEffect: "falls_to_base"
          },
          bayanMaterials: ["copper", "brass", "steel", "wood_possible", "ceramic_or_earthen_possible_by_region"]
        },
        toneProfile: {
          attack: "clear finger strokes",
          sustain: "controlled",
          resonance: "microphone-ready syahi response",
          brightness: "bright",
          noise: "low",
          description: "Stage-focused tabla pair while preserving pressure direction."
        },
        sampleLayers: [drumLayer, resonanceLayer],
        controls: [
          { id: "dayan_zone", label: "Dayan zone", type: "zone", target: "articulation", zones: ["kinar", "chanti", "maidan", "syahi"], public: true, description: "Dayan zones include center/near-center syahi." },
          { id: "bayan_pressure", label: "Bayan pressure", type: "slider", target: "pressure", public: true, min: 0, max: 1, description: "Press raises pitch; release falls to base." }
        ]
      }
    ],
    tunings: [tuning("tabla", "tabla_dayan_tonic_scaffold", "Dayan tonic scaffold", [0, 200, 400, 700, 900, 1200])],
    regionalTuningPresets: [
      preset("tabla", "tabla_bayan_pressure_placeholder", "Bayan pressure placeholder", "South Asian tabla practice", [0, 200, 400, 700, 900, 1200], "Future model should bind bayan pressure curves to physical drum data.")
    ],
    articulations: [
      articulation("tabla", "tabla_zone_pressure", "Tabla zones and pressure", [
        {
          id: "bayan_pressure_pitch",
          label: "Bayan pressure pitch",
          model: "pitch_rises_on_press_release_falls_to_base",
          gesture: "press and release pressure zone",
          description: "Bayan pressure raises pitch on press and returns to base on release."
        }
      ])
    ],
    uiModel: {
      performanceSurface: "drum_heads",
      primaryPlayControlIds: ["dayan_zone", "bayan_pressure"],
      warningPlacement: "right_panel",
      layoutNotes: "Zones must include kinar, chanti, maidan, syahi, and pressure_zone."
    },
    soundDna: {
      attack: "Finger and palm strokes across zones",
      sustain: "Syahi-shaped harmonic drum resonance",
      resonance: "Dayan center/near-center syahi and bayan offset syahi",
      ornamentation: "Bol vocabulary, zone changes, and bayan pressure curves",
      identity: "Material, gharana, and pedagogy data require source-aware mapping"
    }
  },
  {
    id: "mbira",
    name: "Mbira",
    family: "Lamellophone",
    classification: {
      family: "lamellophone",
      familyLabel: "Lamellophone",
      soundProduction: "Plucked metal tines mounted on a soundboard with deze/calabash resonator.",
      regionTags: ["Zimbabwe", "Shona music contexts"],
      primaryMaterials: ["metal tines", "wood soundboard", "deze/calabash resonator"],
      isCulturallySensitive: true
    },
    origin: "Zimbabwe and Shona music contexts",
    shortDescription:
      "Mbira dzavadzimu is a non-linear multi-manual lamellophone, not a six-key kalimba layout.",
    culturalNote:
      "Mbira data must preserve manual roles, spiritual/community contexts, and tuning variation rather than imposing piano-like ordering.",
    culturalCaution:
      "Avoid flattening mbira dzavadzimu into a generic small kalimba; respect context, repertoire, and community use.",
    culturalFlags: [
      {
        id: "mbira-not-six-key-kalimba",
        type: "terminology",
        severity: "warning",
        appliesTo: "variant",
        requiresReview: true,
        message: "Do not implement mbira dzavadzimu as a six-tine kalimba or linear low-to-high keyboard."
      }
    ],
    playableNotes: ["C4", "D4", "E4", "G4", "A4", "C5"],
    variants: [
      {
        id: "mbira_dzavadzimu",
        instrumentId: "mbira",
        name: "Mbira Dzavadzimu",
        priority: "primary",
        region: "Zimbabwe and Shona music contexts",
        construction: "22-28 metal tines on a board, commonly resonated by a deze/calabash.",
        performanceContext: "Ceremonial, community, family, and contemporary performance contexts.",
        distinguishingFeatures: ["22-28 tines", "deze/calabash resonator", "non-linear manuals"],
        tineLayout: {
          tineCountRange: [22, 28],
          resonator: "deze/calabash resonator",
          layoutModel: "non_linear_interlocking_manuals",
          manuals: [
            { id: "left_thumb_lower_manual", role: "left thumb lower manual" },
            { id: "left_thumb_upper_manual", role: "left thumb upper manual" },
            {
              id: "right_thumb_lower_three_note_anchor",
              role: "right thumb lower manual",
              subRole: "low three-note anchor"
            },
            { id: "right_thumb_pattern_extension", role: "right thumb pattern extension" },
            { id: "right_index_upper_response", role: "right index upper response" }
          ]
        },
        toneProfile: {
          attack: "metal tine pluck",
          sustain: "ringing",
          resonance: "deze/calabash amplification and buzz elements",
          brightness: "metallic",
          noise: "medium",
          description: "Interlocking non-linear manual layout with resonator color."
        },
        sampleLayers: [
          { id: "tine_body", role: "body", fallbackOscillator: "triangle", pitchBehavior: "pitched", description: "Metal tine fallback." },
          { id: "deze_resonator", role: "resonance", fallbackOscillator: "sine", pitchBehavior: "pitched", description: "Deze/calabash resonance placeholder." }
        ],
        controls: [
          { id: "left_thumb_lower_manual", label: "Left lower", type: "button", target: "pitch", public: true, description: "Left thumb lower manual." },
          { id: "right_thumb_lower_three_note_anchor", label: "Right anchor", type: "button", target: "pitch", public: true, description: "Right thumb low three-note anchor." }
        ]
      }
    ],
    tunings: [tuning("mbira", "mbira_non_linear_scaffold", "Non-linear mbira scaffold", [0, 182, 386, 702, 884, 1200])],
    regionalTuningPresets: [
      preset("mbira", "mbira_dzavadzimu_placeholder", "Dzavadzimu tuning placeholder", "Zimbabwe", [0, 182, 386, 702, 884, 1200], "Actual mbira tunings vary by maker and context and should be source-reviewed.")
    ],
    articulations: [
      articulation("mbira", "mbira_manuals", "Mbira manual roles", [
        {
          id: "right_thumb_anchor",
          label: "Right thumb anchor",
          model: "right_thumb_lower_three_note_anchor",
          gesture: "right thumb lower manual trigger",
          description: "Right thumb lower role anchors three lower notes, not a linear piano ordering."
        }
      ])
    ],
    uiModel: {
      performanceSurface: "tine_manuals",
      primaryPlayControlIds: ["left_thumb_lower_manual", "right_thumb_lower_three_note_anchor"],
      warningPlacement: "right_panel",
      layoutNotes: "Render interlocking manuals and avoid keyboard ordering."
    },
    soundDna: {
      attack: "Plucked metal tine",
      sustain: "Ringing lamella decay",
      resonance: "Deze/calabash resonator and buzz color",
      ornamentation: "Interlocking manual patterns",
      identity: "Manual roles and community context are structural, not optional decoration"
    }
  },
  {
    id: "kora",
    name: "Kora",
    family: "Plucked harp-lute",
    classification: {
      family: "plucked_harp_lute",
      familyLabel: "Plucked harp-lute",
      soundProduction: "Two ranks of plucked strings on a calabash bridge harp-lute.",
      regionTags: ["Mande cultures", "West Africa", "diaspora"],
      primaryMaterials: ["calabash", "skin", "wood", "strings"],
      isCulturallySensitive: true
    },
    origin: "Mande jali/griot musical lineages in West Africa",
    shortDescription:
      "A harp-lute associated with jali/griot lineages, interlocking patterns, and narrative performance.",
    culturalNote:
      "Kora data should name jali/griot context carefully and avoid flattening Mande musical authority into a generic West African sound.",
    culturalCaution:
      "Treat jali/griot lineage, repertoire, and oral-history context as part of the instrument identity.",
    culturalFlags: [
      {
        id: "kora-jali-context",
        type: "cultural_sensitivity",
        severity: "warning",
        appliesTo: "instrument",
        requiresReview: true,
        message: "Griot/jali cultural context must stay visible in UI and documentation."
      }
    ],
    playableNotes: ["C4", "D4", "E4", "G4", "A4", "C5"],
    variants: [
      {
        id: "traditional_21_kora",
        instrumentId: "kora",
        name: "21-string Traditional Kora",
        priority: "primary",
        region: "Mali, Senegal, Gambia, Guinea, and neighboring regions",
        construction: "21 strings in two ranks over a bridge on a calabash resonator.",
        performanceContext: "Jali/griot repertoire, praise song, solo, and accompaniment settings.",
        distinguishingFeatures: ["21 strings", "two ranks", "calabash resonator", "jali/griot context"],
        stringProfile: { stringCount: 21, material: "nylon/gut/fishing-line traditions vary by context" },
        soundbox: { shape: "calabash", membrane: "skin" },
        toneProfile: {
          attack: "bright pluck",
          sustain: "ringing",
          resonance: "calabash body",
          brightness: "bright",
          noise: "medium",
          description: "Interlocking thumb/index patterns and calabash bloom."
        },
        sampleLayers: [pluckedBodyLayer, resonanceLayer],
        controls: [notePadControl]
      },
      {
        id: "keur_moussa_kora",
        instrumentId: "kora",
        name: "Keur Moussa Kora",
        priority: "secondary",
        region: "Senegal and Catholic liturgical adaptation contexts",
        construction: "Kora adaptation associated with Keur Moussa practice.",
        performanceContext: "Liturgical and adapted ensemble contexts.",
        distinguishingFeatures: ["adapted tuning context", "kora body", "specific institutional lineage"],
        stringProfile: { stringCount: 21, material: "modern string materials vary" },
        soundbox: { shape: "calabash", membrane: "skin" },
        toneProfile: {
          attack: "clear pluck",
          sustain: "ringing",
          resonance: "calabash body",
          brightness: "balanced",
          noise: "low",
          description: "Adapted context stored separately from traditional jali/griot practice."
        },
        sampleLayers: [pluckedBodyLayer, resonanceLayer],
        controls: [notePadControl]
      },
      {
        id: "electric_modern_kora",
        instrumentId: "kora",
        name: "Electric / Modern Kora",
        priority: "secondary",
        region: "Global contemporary stages",
        construction: "Modern kora with pickups or stage hardware while retaining harp-lute layout.",
        performanceContext: "Amplified ensembles, fusion, studio, and stage contexts.",
        distinguishingFeatures: ["pickup-ready", "modern hardware", "stage adaptation"],
        stringProfile: { stringCount: 21, material: "modern strings" },
        soundbox: { shape: "calabash", membrane: "skin" },
        toneProfile: {
          attack: "clear amplified pluck",
          sustain: "controlled",
          resonance: "pickup plus resonator blend",
          brightness: "bright",
          noise: "low",
          description: "Stage-focused kora data while preserving cultural context."
        },
        sampleLayers: [pluckedBodyLayer, resonanceLayer],
        controls: [notePadControl]
      }
    ],
    tunings: [tuning("kora", "kora_tomora_ba_scaffold", "Tomora Ba scaffold", [0, 204, 386, 702, 884, 1200])],
    regionalTuningPresets: [
      preset("kora", "kora_sauta_placeholder", "Sauta placeholder", "Mande kora practice", [0, 204, 386, 702, 884, 1200], "Named tuning families require performer/source review.")
    ],
    articulations: [
      articulation("kora", "kora_interlock", "Kora interlocking patterns", [
        {
          id: "thumb_index_interlock",
          label: "Thumb/index interlock",
          model: "two_rank_harp_lute_interlock",
          gesture: "alternating rank triggers",
          description: "Two-rank pattern behavior should guide the UI surface."
        }
      ])
    ],
    uiModel: {
      performanceSurface: "harp_lute_ranks",
      primaryPlayControlIds: ["note_pad"],
      warningPlacement: "right_panel",
      layoutNotes: "Future UI should show two ranks rather than a linear keyboard."
    },
    soundDna: {
      attack: "Bright harp-lute pluck",
      sustain: "Interlocking ringing decay",
      resonance: "Calabash resonator and skin surface",
      ornamentation: "Thumb/index rank patterns and melodic variation",
      identity: "Jali/griot context and oral history are part of the data model"
    }
  },
  {
    id: "yidaki_didgeridoo",
    name: "Yidaki / Didgeridoo",
    family: "Drone aerophone",
    classification: {
      family: "drone_aerophone",
      familyLabel: "Drone aerophone",
      soundProduction: "Lip-driven drone tube with vocal formants and mouth-shape filtering.",
      regionTags: ["Arnhem Land", "Australia", "global contemporary practice"],
      primaryMaterials: ["wood", "termite-hollowed timber", "modern materials"],
      isCulturallySensitive: true
    },
    origin: "Aboriginal Australian yidaki contexts and global didgeridoo practice",
    shortDescription:
      "A drone aerophone where traditional Yidaki and modern Didgeridoo contexts must be distinguished.",
    culturalNote:
      "Traditional Yidaki can carry strong cultural protocols. Timbroam should avoid presenting restricted knowledge or flattening all didgeridoo practice into one context.",
    culturalCaution:
      "Cultural sensitivity warning: distinguish Traditional Yidaki from modern didgeridoo, and do not imply access to restricted ceremonial knowledge.",
    culturalFlags: [
      {
        id: "yidaki-cultural-protocol",
        type: "cultural_sensitivity",
        severity: "restricted",
        appliesTo: "instrument",
        requiresReview: true,
        message: "Traditional Yidaki content needs cultural protocol review before detailed implementation."
      }
    ],
    playableNotes: ["C2", "D2", "E2", "G2", "A2", "C3"],
    variants: [
      {
        id: "traditional_yidaki",
        instrumentId: "yidaki_didgeridoo",
        name: "Traditional Yidaki",
        priority: "primary",
        region: "Arnhem Land cultural contexts",
        construction: "Wooden drone tube associated with specific cultural protocols.",
        performanceContext: "Culturally governed contexts requiring review and permission-aware documentation.",
        distinguishingFeatures: ["traditional yidaki context", "cultural protocol warning", "drone tube"],
        toneProfile: {
          attack: "lip buzz onset",
          sustain: "continuous drone",
          resonance: "tube resonance and vocal tract",
          brightness: "dark",
          noise: "medium",
          description: "Drone and vocal formant controls are technical placeholders only."
        },
        sampleLayers: [
          { id: "drone", role: "drone", fallbackOscillator: "sawtooth", pitchBehavior: "fixed", description: "Base drone layer." },
          { id: "vocal_formant", role: "resonance", fallbackOscillator: "sine", pitchBehavior: "formant", description: "Vocal formant placeholder." }
        ],
        controls: [
          { id: "drone_level", label: "Drone", type: "slider", target: "drone", public: true, min: 0, max: 1, description: "Base drone intensity." },
          { id: "vocal_formant", label: "Vocal formant", type: "slider", target: "formant", public: true, min: 0, max: 1, description: "Vocal tract formant emphasis." },
          { id: "mouth_shape_xy", label: "Mouth shape", type: "xy_pad", target: "formant", public: true, description: "XY pad for vowel-like mouth shape filtering." }
        ]
      },
      {
        id: "modern_didgeridoo",
        instrumentId: "yidaki_didgeridoo",
        name: "Modern Didgeridoo",
        priority: "secondary",
        region: "Global contemporary practice",
        construction: "Drone tube using wood or modern materials in contemporary performance contexts.",
        performanceContext: "Contemporary, educational, and experimental music contexts.",
        distinguishingFeatures: ["modern context", "drone tube", "vocal formants"],
        toneProfile: {
          attack: "lip buzz onset",
          sustain: "continuous drone",
          resonance: "tube resonance and mouth filtering",
          brightness: "balanced",
          noise: "medium",
          description: "Modern didgeridoo control surface separated from Traditional Yidaki."
        },
        sampleLayers: [
          { id: "drone", role: "drone", fallbackOscillator: "sawtooth", pitchBehavior: "fixed", description: "Base drone layer." },
          { id: "vocal_formant", role: "resonance", fallbackOscillator: "sine", pitchBehavior: "formant", description: "Vocal formant placeholder." }
        ],
        controls: [
          { id: "drone_level", label: "Drone", type: "slider", target: "drone", public: true, min: 0, max: 1, description: "Base drone intensity." },
          { id: "vocal_formant", label: "Vocal formant", type: "slider", target: "formant", public: true, min: 0, max: 1, description: "Vocal tract formant emphasis." },
          { id: "mouth_shape_xy", label: "Mouth shape", type: "xy_pad", target: "formant", public: true, description: "XY pad for vowel-like mouth shape filtering." }
        ]
      }
    ],
    tunings: [tuning("yidaki_didgeridoo", "yidaki_drone_scaffold", "Drone fundamental scaffold", [0, 120, 240, 360, 480, 600])],
    regionalTuningPresets: [
      preset("yidaki_didgeridoo", "yidaki_drone_placeholder", "Drone placeholder", "Arnhem Land and global contexts", [0, 120, 240, 360, 480, 600], "Future implementation must distinguish instrument-specific fundamentals and cultural protocol limits.")
    ],
    articulations: [
      articulation("yidaki_didgeridoo", "yidaki_drone_formant", "Drone and formant controls", [
        {
          id: "mouth_shape_xy",
          label: "Mouth shape XY",
          model: "vocal_formant_xy_filter",
          gesture: "XY pad",
          description: "Mouth-shape XY pad controls vocal formant filtering over the drone."
        }
      ])
    ],
    uiModel: {
      performanceSurface: "drone_pipe",
      primaryPlayControlIds: ["drone_level", "vocal_formant", "mouth_shape_xy"],
      warningPlacement: "right_panel",
      layoutNotes: "Cultural caution must remain visible for traditional yidaki contexts."
    },
    soundDna: {
      attack: "Lip-driven drone onset",
      sustain: "Continuous circular-breath-style drone model",
      resonance: "Tube resonance plus vocal formants",
      ornamentation: "Mouth shape, vocal formant, and rhythmic drone articulation",
      identity: "Traditional Yidaki and modern didgeridoo are separate cultural branches"
    }
  },
  {
    id: "gamelan_saron",
    name: "Gamelan Saron",
    family: "Bronze idiophone",
    classification: {
      family: "bronze_idiophone",
      familyLabel: "Bronze idiophone",
      soundProduction: "Struck bronze bars with damping and ensemble-specific tuning.",
      regionTags: ["Java", "Bali", "Indonesia", "gamelan ensembles"],
      primaryMaterials: ["bronze bars", "wooden frame"]
    },
    origin: "Indonesian gamelan ensembles",
    shortDescription:
      "A bronze-bar gamelan instrument whose tuning belongs to a specific ensemble, with ombak and damping behavior.",
    culturalNote:
      "Actual gamelan tuning differs by ensemble. Generic slendro or pelog approximations are warnings, not authoritative presets.",
    culturalFlags: [
      {
        id: "gamelan-ensemble-specific-tuning",
        type: "approximation",
        severity: "warning",
        appliesTo: "tuning",
        requiresReview: true,
        message: "Actual gamelan tuning is ensemble-specific; generic slendro/pelog approximations must show warnings."
      }
    ],
    playableNotes: ["C4", "D4", "E4", "G4", "A4", "C5"],
    variants: [
      {
        id: "saron_barung",
        instrumentId: "gamelan_saron",
        name: "Saron Barung",
        priority: "primary",
        region: "Javanese gamelan contexts",
        construction: "Bronze bars over a resonant frame, struck and damped by hand.",
        performanceContext: "Balungan and ensemble texture contexts.",
        distinguishingFeatures: ["bronze bars", "damping on keyup", "ensemble-specific tuning"],
        physicalMetadata: { ombakDualLayer: true, dampingModel: "keyup" },
        toneProfile: {
          attack: "mallet strike",
          sustain: "short after damping",
          resonance: "bronze bar and frame",
          brightness: "metallic",
          noise: "medium",
          description: "Damping keyup model and ombak dual layer are required."
        },
        sampleLayers: [
          bronzeLayer,
          { id: "ombak_dual_layer", role: "ombak", fallbackOscillator: "sine", pitchBehavior: "micro_detuned", description: "Dual-layer beating placeholder for ombak." }
        ],
        controls: [
          { id: "bar_key", label: "Bronze bar", type: "button", target: "pitch", public: true, description: "Strike bronze bar." },
          { id: "keyup_damping", label: "Damping", type: "gesture", target: "damping", gesture: "keyup", public: true, description: "Release/key-up triggers damping." }
        ]
      },
      {
        id: "saron_panerus",
        instrumentId: "gamelan_saron",
        name: "Saron Panerus / Peking",
        priority: "secondary",
        region: "Javanese gamelan contexts",
        construction: "Higher saron-family bronze bars over a resonant frame.",
        performanceContext: "Higher-register balungan elaboration contexts.",
        distinguishingFeatures: ["bronze bars", "higher register", "damping on keyup", "ensemble-specific tuning"],
        physicalMetadata: { ombakDualLayer: true, dampingModel: "keyup" },
        toneProfile: {
          attack: "bright mallet strike",
          sustain: "short after damping",
          resonance: "higher bronze bar response",
          brightness: "metallic",
          noise: "medium",
          description: "Higher saron-family variant with the same tuning caution."
        },
        sampleLayers: [
          bronzeLayer,
          { id: "ombak_dual_layer", role: "ombak", fallbackOscillator: "sine", pitchBehavior: "micro_detuned", description: "Dual-layer beating placeholder for ombak." }
        ],
        controls: [
          { id: "bar_key", label: "Bronze bar", type: "button", target: "pitch", public: true, description: "Strike bronze bar." },
          { id: "keyup_damping", label: "Damping", type: "gesture", target: "damping", gesture: "keyup", public: true, description: "Release/key-up triggers damping." }
        ]
      }
    ],
    tunings: [
      tuning(
        "gamelan_saron",
        "genericSlendroApprox",
        "Generic Slendro Approximation",
        [0, 240, 480, 720, 960, 1200],
        undefined,
        "Approximation only. Slendro tuning is not universal and varies by ensemble."
      ),
      tuning(
        "gamelan_saron",
        "genericPelogApprox",
        "Generic Pelog Approximation",
        [0, 140, 300, 520, 700, 1200],
        undefined,
        "Approximation only. Pelog tuning is not universal and varies by ensemble."
      )
    ],
    regionalTuningPresets: [
      preset(
        "gamelan_saron",
        "genericSlendroApprox",
        "Generic Slendro Approximation",
        "Java/Bali ensemble-dependent",
        [0, 240, 480, 720, 960, 1200],
        "Approximation warning required: actual gamelan tuning is ensemble-specific."
      ),
      preset(
        "gamelan_saron",
        "genericPelogApprox",
        "Generic Pelog Approximation",
        "Java/Bali ensemble-dependent",
        [0, 140, 300, 520, 700, 1200],
        "Approximation warning required: actual gamelan tuning is ensemble-specific."
      )
    ],
    articulations: [
      articulation("gamelan_saron", "saron_strike_damp", "Saron strike and damping", [
        {
          id: "keyup_damping",
          label: "Key-up damping",
          model: "damping_on_keyup",
          gesture: "release key or pad",
          description: "The bar is damped on keyup/release, not by passive envelope alone."
        },
        {
          id: "ombak",
          label: "Ombak",
          model: "dual_micro_detuned_layer",
          gesture: "layer mix control",
          description: "Dual layer beating model for ombak approximation."
        }
      ])
    ],
    uiModel: {
      performanceSurface: "bronze_bar_keys",
      primaryPlayControlIds: ["bar_key", "keyup_damping"],
      warningPlacement: "right_panel",
      layoutNotes: "Always show ensemble-specific tuning warning when generic approximations are used."
    },
    soundDna: {
      attack: "Mallet strike on bronze bars",
      sustain: "Damped metallic resonance",
      resonance: "Bronze bar and frame resonance with ombak beating",
      ornamentation: "Manual damping and ensemble texture",
      identity: "Tuning belongs to an ensemble; generic slendro/pelog data is approximate"
    }
  }
];

export const instruments: Instrument[] = instrumentsData;

export function getInstrumentById(instrumentId: string): Instrument {
  return instruments.find((instrument) => instrument.id === instrumentId) ?? instruments[0];
}

export function getDefaultVariantId(instrumentId: string): string {
  return getInstrumentById(instrumentId).variants[0]?.id ?? "";
}

export function getDefaultTuningId(instrumentId: string): string {
  return getInstrumentById(instrumentId).tunings[0]?.id ?? "";
}

export function getVariantById(instrumentId: string, variantId: string): InstrumentVariant {
  const instrument = getInstrumentById(instrumentId);

  return instrument.variants.find((variant) => variant.id === variantId) ?? instrument.variants[0];
}

export function getTuningById(instrumentId: string, tuningId: string): Tuning {
  const instrument = getInstrumentById(instrumentId);

  return instrument.tunings.find((tuningProfile) => tuningProfile.id === tuningId) ?? instrument.tunings[0];
}
